import React from "react";
import { Button } from "@/components/ui/button";
import { Eye } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { ClientFactory } from "@/main/common/ClientFactory";
import { MetaHandler } from "@/main/common/MetaHandler";
import { getSensitivePreferenceMode, shouldMaskSensitiveMedia } from "@/main/common/SensitivePreference";
import { goBackToPreviousEntry } from "@/main/navigation/NavigationHistoryState";
import { openAccountAccessDialog } from "@/main/react/account_access/AccountAccessDisplayerDialog";
import {
    ACCOUNT_ACCESS_DIALOG_COPY,
    resolveContextualAccountAccessDialogOptions,
    withAccountAccessSurface,
} from "@/main/react/account_access/AccountAccessDialogContext";
import { getCurrentRelativeUrl } from "@/main/react/account_access/AccountAccessContinuation";
import type { Localizer } from "@/main/react/Types";
import { getPageContentWarningCopy } from "@/main/react/sensitive/ContentWarningCopy";
import type { SensitiveUnlockKind } from "@/main/common/SensitiveUnlockStore";
import SensitiveBadge from "@/main/react/sensitive/SensitiveBadge";
import type { UpdateContentPreferencesRequest } from "@/generated-client";
import { toast } from "sonner";

/**
 * `PROFILE-57` (V29 a, "a bottom fade"). The sheet scrolls under `no-scrollbar`, so when the panel
 * outgrows the phone (200% text, the longer locales) nothing said its exits were below the fold. A
 * CSS-only scroll cue on THIS call site, not in the shared primitive: a `--popover` cover attached
 * `local` (it travels with the content and sits at its end) over a `--border` fade attached `scroll`
 * (pinned to the visible lower edge). While content remains below, the cover is out of view and the fade
 * shows; at the end of the content, or when nothing overflows, the cover hides it. Background layers
 * paint under the content, so no control is covered. The visual is owed to VERIFY: jsdom cannot scroll.
 */
export const GATE_SCROLL_CUE_CLASS = "[background-image:linear-gradient(transparent,var(--popover)_75%),linear-gradient(to_top,var(--border),transparent)] [background-size:100%_2.5rem,100%_0.75rem] [background-position:bottom,bottom] [background-repeat:no-repeat] [background-attachment:local,scroll]";

type SensitivePageGateProps = {
    isSensitive?: boolean | null;
    isSpoiler?: boolean | null;
    hideSensitiveMedia?: boolean;
    /**
     * Controlled reveal state, owned by the page via `useSensitivePageUnlock`. The page needs this same
     * boolean to stop masking its nested cards, so it cannot live in here.
     */
    revealed: boolean;
    onReveal: () => void;
    className?: string;
    contentClassName?: string;
    localizer: Localizer;
    /**
     * Which kind of thing is behind the scrim, so the help line can name it instead of saying "page"
     * a second time (`PROFILE-4`). Optional; the generic line is the fallback.
     */
    subjectKind?: SensitiveUnlockKind | null;
    /**
     * WHOSE (or WHICH) thing this gate is standing in front of — a handle, a title (`PROFILE-4`).
     *
     * <p>The gate blurs and `aria-hidden`s everything it covers, identity strip included, and every
     * string it shows says "page". So a visitor was asked to consent with the subject deliberately
     * withheld: someone sent the wrong link could not discover that except by revealing content they
     * may not want, and someone who would have been fine bounced via "Go home" because they could not
     * confirm they were in the right place. The only identifying text on screen was the URL.
     *
     * <p>Not localized copy and deliberately not labelled: it is the name itself, in whatever form the
     * surface's own header uses, so it costs no catalog key and reads the same in every locale. It is
     * also not sensitive payload — it is in the URL, in the document title, and in the header this
     * gate is covering.
     *
     * <p>Optional: a surface that passes nothing renders exactly as before.
     */
    subjectLabel?: React.ReactNode;
    badgeLabel?: React.ReactNode;
    title?: React.ReactNode;
    helperText?: React.ReactNode;
    revealText?: React.ReactNode;
    goHomeText?: React.ReactNode;
    enablePreferenceText?: React.ReactNode;
    enablePreferenceSavingText?: React.ReactNode;
    enablePreferenceSuccessText?: React.ReactNode;
    enablePreferenceErrorText?: React.ReactNode;
    showEnablePreferenceButton?: boolean;
    revealButtonDataPlaywrightId?: string;
    goHomeButtonDataPlaywrightId?: string;
    enablePreferenceButtonDataPlaywrightId?: string;
    children: React.ReactNode;
};

function buildDefaultHomeHref() {
    if (typeof window === "undefined") {
        return "/";
    }

    const homeUrl = new URL("/", window.location.origin);
    const currentUrl = new URL(window.location.href);
    const lang = currentUrl.searchParams.get("lang");

    if (lang) {
        homeUrl.searchParams.set("lang", lang);
    }

    return `${homeUrl.pathname}${homeUrl.search}${homeUrl.hash}`;
}

export default function SensitivePageGate({
    isSensitive = false,
    isSpoiler = false,
    hideSensitiveMedia = true,
    revealed,
    onReveal,
    className,
    contentClassName,
    localizer,
    subjectKind,
    subjectLabel,
    badgeLabel,
    title,
    helperText,
    revealText,
    goHomeText,
    enablePreferenceText,
    enablePreferenceSavingText,
    enablePreferenceSuccessText,
    enablePreferenceErrorText,
    showEnablePreferenceButton,
    revealButtonDataPlaywrightId,
    goHomeButtonDataPlaywrightId = "sensitive-page-gate-go-home",
    enablePreferenceButtonDataPlaywrightId = "sensitive-page-gate-enable-preference",
    children,
}: SensitivePageGateProps) {
    const api = React.useMemo(() => ClientFactory.getAjaxSettingsControllerApi(), []);
    const [effectiveHideSensitiveMedia, setEffectiveHideSensitiveMedia] = React.useState(Boolean(hideSensitiveMedia));
    const [savingPreference, setSavingPreference] = React.useState(false);
    const maskedBySensitivePreference = shouldMaskSensitiveMedia(Boolean(isSensitive), effectiveHideSensitiveMedia);
    const shouldGate = Boolean(isSpoiler) || maskedBySensitivePreference;
    const homeHref = React.useMemo(() => buildDefaultHomeHref(), []);
    const defaultPageWarningCopy = React.useMemo(
        () => getPageContentWarningCopy(localizer, { isSensitive, isSpoiler, kind: subjectKind }),
        [isSensitive, isSpoiler, localizer, subjectKind],
    );
    const resolvedBadgeLabel = badgeLabel ?? defaultPageWarningCopy.badgeLabel;
    const resolvedTitle = title ?? defaultPageWarningCopy.title;
    const resolvedHelperText = helperText ?? defaultPageWarningCopy.helperText;
    const resolvedRevealText = revealText ?? defaultPageWarningCopy.revealText;
    const resolvedGoHomeText = goHomeText ?? localizer("sensitive.page-warning.go-home");
    const resolvedEnablePreferenceText = enablePreferenceText ?? localizer("sensitive.page-warning.enable-preference");
    const resolvedEnablePreferenceSavingText = enablePreferenceSavingText ?? localizer("sensitive.page-warning.enable-preference.saving");
    const resolvedEnablePreferenceSuccessText = enablePreferenceSuccessText ?? localizer("sensitive.page-warning.enable-preference.success");
    const resolvedEnablePreferenceErrorText = enablePreferenceErrorText ?? localizer("sensitive.page-warning.enable-preference.error");
    const shouldShowEnablePreferenceButton = showEnablePreferenceButton
        ?? (defaultPageWarningCopy.showSensitivePreferenceAction && maskedBySensitivePreference);
    const isLoggedIn = typeof document !== "undefined" && typeof MetaHandler.isUserLoggedIn === "function"
        ? MetaHandler.isUserLoggedIn()
        : false;
    // PROFILE-6: the reveal action is the page-scoped one, so it is the one the reflex Enter on a
    // blocking modal must land on. Radix autofocuses the first tabbable child otherwise, which was
    // "Always show" — the single most consequential control on the screen.
    const revealButtonRef = React.useRef<HTMLButtonElement | null>(null);

    /**
     * `PROFILE-19`. Whether there is anywhere to go back TO, decided once at mount.
     *
     * <p>`history.length <= 1` is the only synchronously-observable form of "nothing behind us" — a
     * refused traversal is silent and asynchronous — and it is the same predicate
     * {@link goBackToPreviousEntry} uses for its own fallback. Reading it here as well is what keeps a
     * cold entry (a pasted URL, an external referrer, a new tab from a search result) from being shown
     * a control that would only ever behave like the "Go home" already beside it.
     */
    const hasSomewhereToGoBackTo = React.useMemo(() => {
        if (typeof window === "undefined") {
            return false;
        }
        try {
            return window.history.length > 1;
        } catch {
            return false;
        }
    }, []);

    /** The one non-consenting exit, shared by the Back control, Escape and the scrim (`PROFILE-56`/`-58`). */
    const goBack = React.useCallback(() => {
        goBackToPreviousEntry({ fallbackUrl: homeHref });
    }, [homeHref]);

    React.useEffect(() => {
        setEffectiveHideSensitiveMedia(Boolean(hideSensitiveMedia));
    }, [hideSensitiveMedia]);

    const blocked = shouldGate && !revealed;
    const handleEnablePreference = React.useCallback(async () => {
        if (savingPreference) {
            return;
        }

        setSavingPreference(true);
        // `PROFILE-39` (S7 C36): the preference this click replaces, read BEFORE the write, so the
        // toast's Undo can put back exactly what the member had (blur or hide), not a guessed default.
        const previousPreference = getSensitivePreferenceMode();
        try {
            if (isLoggedIn) {
                const payload = {
                    hideSensitiveMedia: false,
                    sensitivePreference: "SHOW",
                } as UpdateContentPreferencesRequest & { sensitivePreference: "SHOW" };
                await api.updateContentPreferences(payload);
            }

            MetaHandler.setSensitivePreference("show");
            setEffectiveHideSensitiveMedia(false);
            onReveal();
            toast.success(resolvedEnablePreferenceSuccessText, previousPreference === "show" ? undefined : {
                action: {
                    // G-01: a reversible change on one member gets an undo toast. The page this member
                    // just opened stays open; only the site-wide preference goes back.
                    label: <span data-playwright-id="sensitive-page-gate-enable-preference-undo">{localizer("literal.undo")}</span>,
                    onClick: () => {
                        void (async () => {
                            try {
                                if (isLoggedIn) {
                                    const restore: UpdateContentPreferencesRequest = {
                                        hideSensitiveMedia: true,
                                        sensitivePreference: previousPreference === "hide" ? "HIDE" : "BLUR",
                                    };
                                    await api.updateContentPreferences(restore);
                                }
                                MetaHandler.setSensitivePreference(previousPreference);
                                setEffectiveHideSensitiveMedia(true);
                            } catch {
                                toast.error(resolvedEnablePreferenceErrorText);
                            }
                        })();
                    },
                },
            });
        } catch {
            toast.error(resolvedEnablePreferenceErrorText);
        } finally {
            setSavingPreference(false);
        }
    }, [api, isLoggedIn, onReveal, resolvedEnablePreferenceErrorText, resolvedEnablePreferenceSuccessText, savingPreference]);

    return (
        <div className={cn("relative w-full", className)} data-sensitive-page-gate={blocked ? "blocked" : "visible"}>
            <div
                className={cn(
                    blocked && "pointer-events-none select-none blur-sm brightness-75 transition duration-200 ease-out",
                    contentClassName,
                )}
                aria-hidden={blocked ? "true" : undefined}
            >
                {children}
            </div>

            <Dialog open={blocked}>
                <DialogContent
                    showCloseButton={false}
                    /* `PROFILE-56` / `PROFILE-58` (V29 a). Escape and a tap on the scrim were suppressed
                       while the only exit was a navigation home: dismissing must never read as consent.
                       `PROFILE-19` has since added a non-consenting exit, Back, so when (and only when)
                       it renders, both gestures take it. The dialog is never closed by them, nothing is
                       revealed and no preference is written; on a cold entry both stay inert.

                       `onPointerDownOutside`, not `onInteractOutside`: the latter also fires on FOCUS
                       leaving the panel, which is what happens when the inline Log In opens the
                       account-access sheet, and that must not navigate the visitor away. A press inside
                       another dialog or a toast is not a tap on this scrim either.

                       Both act only while `blocked`: Radix keeps the sheet mounted through its exit
                       animation and still reports outside presses for it, so without the check the
                       member's first tap on the page they just revealed sent them Back (devo gate
                       `2d91512ac0`, 2026-09-29). */
                    onEscapeKeyDown={(event) => {
                        event.preventDefault();
                        if (blocked && hasSomewhereToGoBackTo) {
                            goBack();
                        }
                    }}
                    onPointerDownOutside={(event) => {
                        event.preventDefault();
                        const target = event.target instanceof Element ? event.target : null;
                        if (blocked && hasSomewhereToGoBackTo && !target?.closest('[role="dialog"], [role="alertdialog"], [data-sonner-toaster]')) {
                            goBack();
                        }
                    }}
                    onInteractOutside={(event) => event.preventDefault()}
                    onOpenAutoFocus={(event) => {
                        event.preventDefault();
                        revealButtonRef.current?.focus();
                    }}
                    /* PROFILE-2 — two defects in one className, both from overriding the primitive.
                       `bg-background1/95` repainted the panel in the PAGE's own base colour, which in
                       dark measured 1.01:1 against the dimmed backdrop: the one modal in the product
                       whose whole job is to be noticed dissolved into the page it was blocking. And
                       `shadow-2xl` is a black shadow on a near-black ground, so nothing was left to
                       separate them but a 1.53:1 hairline. `DialogContent` already carries `bg-popover`
                       and `--surface-shadow-lg`, both tuned per theme, so the fix is to stop fighting
                       it. The width cap comes down from 894px painted (sm:max-w-3xl / lg:max-w-4xl) to
                       a max near the longest line, kept as a MAX because several locales run longer
                       than English here. */
                    /* PROFILE-11 — the same deletion move PROFILE-2 made one override earlier, for
                       the same reason: this call site was fighting the primitive and losing three
                       ways at once. It passes no `mobilePresentation`, so it takes the default
                       `"sheet"` branch, which already emits
                       `inset-x-0 bottom-0 max-w-none … rounded-t-[1.75rem] px-4 pt-4
                       pb-[calc(1rem+env(safe-area-inset-bottom))]` (dialog.tsx:125). `className`
                       merges last through twMerge, so the removed string defeated all three:
                       an explicit `w-`/`max-w-` under `inset-x-0` OVER-CONSTRAINS the box, which then
                       resolves at `left: 0` and collects the whole gutter on one side (measured 1.1
                       CSS px left, 17.5 right); `rounded-[1.75rem]` replaced `rounded-t-*`, drawing a
                       sheet with rounded bottom corners sitting on the viewport floor; and `p-5`
                       replaced the safe-area bottom padding (measured 19.4 CSS px flat, no inset
                       term), putting "Go home" — the DECLINE option — inside the system gesture zone
                       on any phone with a home indicator.
                       Only the `sm:` tier survives, and it is the part PROFILE-2 deliberately added:
                       a max near the longest line, kept as a MAX because several locales run longer
                       than English here. The `md:` rung spells the SAME value rather than leaving it
                       to `DialogContent`'s own `md:max-w-xl` — twMerge does not merge across
                       breakpoint modifiers, so a half-ladder is correct only by coincidence and
                       drifts the moment the primitive is retuned (F17, 2026-09-04). The 529px-vs-520px
                       button-wrap measurement below is taken at this same 576px cap. */
                    className={cn("sm:max-w-xl md:max-w-xl sm:p-7", GATE_SCROLL_CUE_CLASS)}
                >
                    <DialogHeader className="items-center gap-4 text-center sm:text-center">
                        <SensitiveBadge className="px-3 py-1 text-sm">{resolvedBadgeLabel}</SensitiveBadge>
                        {/* PROFILE-4 — the subject, above the prompt that asks the visitor to consent
                            to it. Between the badge and the title on purpose: the badge says what kind
                            of decision this is, this says what it is ABOUT, and the title says what to
                            do. See the prop's docs for why it is not a localized string. */}
                        {subjectLabel ? (
                            <p
                                className="max-w-2xl text-sm font-medium wrap-anywhere text-muted-foreground"
                                data-playwright-id="sensitive-page-gate-subject"
                            >
                                {subjectLabel}
                            </p>
                        ) : null}
                        <DialogTitle className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
                            {resolvedTitle}
                        </DialogTitle>
                        {resolvedHelperText ? (
                            <DialogDescription className="max-w-2xl text-sm leading-6 text-muted-foreground">
                                {resolvedHelperText}
                            </DialogDescription>
                        ) : null}
                    </DialogHeader>
                    {/* PROFILE-8 — DOM order is the ORDER THIS IS HEARD IN, primary first, and CSS
                        produces both visual arrangements from it. `DialogFooter`'s own
                        `flex-col-reverse` painted mobile top-to-bottom as the exact reverse of focus,
                        screen-reader and switch-control order, so the sighted phone user and the
                        keyboard user on the same phone were handed opposite defaults for one blocking
                        decision. Fixed HERE and not in the primitive on purpose: `DialogFooter` is
                        shared by every multi-button dialog in the product, and inverting all of them is
                        a component-level decision, recorded as wave 2 in the remediation design.

                        PROFILE-7 — all three take the repo's existing coarse-pointer floor rather than a
                        new one. `h-auto` stays: it is what lets a long localized label wrap, so the
                        floor has to be a MINIMUM height, and it has to be pointer-conditional rather
                        than viewport-conditional because this row is `w-full` stacked on a phone and
                        `sm:w-auto` inline on a desktop. Pinned by `tapTargets.contract.test.tsx`.

                        PROFILE-16 — the desktop arrangement is produced by `sm:order-*`, NOT by
                        `sm:flex-row-reverse`, and that is the whole of this row's fix. Under
                        `flex-wrap` a reversed row fills its lines in DOM order and only then reverses
                        WITHIN each line, so as soon as the three buttons stopped fitting — which they
                        do in English at `sm:max-w-xl`, measured 529px of content against 520px
                        available — the wrap fell after DOM children 1-2 and painted
                        `[Always show everywhere] [Continue]` on line 1 with `[Go home]` alone below.
                        That put the one irreversible action of the screen in the leftmost slot, the
                        one dialog muscle memory reads as the cheap way out, while a keyboard or
                        screen-reader user still met `Continue` first — i.e. `PROFILE-8`'s guarantee
                        held only at widths where the row did not wrap.

                        Flexbox fills wrap lines in ORDER-MODIFIED order, so ranking the three by
                        consequence (exit 1, product-wide 2, page-scoped primary 3) makes the leftmost
                        control `Go home` whether the row wraps or not. When it does fit the painted
                        result is byte-identical to what `flex-row-reverse` produced, so this changes
                        the wrap case only. DOM order is untouched — it is still primary-first, which
                        is `PROFILE-8`. Below `sm` neither the direction nor the order applies and the
                        stack is unchanged.

                        PROFILE-18 — the phone stack widens ONE neighbour pair, not the whole stack.
                        `mt-2` on the preference button below adds to the container `gap-2`, so the
                        distance between the reversible primary and this product-wide action — the one
                        a signed-out visitor cannot undo in-product — becomes 16 CSS px while every
                        other pair keeps 8. Widening `gap-2` itself would have moved the two safe
                        controls apart for nothing. Its `sm:mt-0` half is load-bearing rather than
                        tidy: at `sm` the footer is a row with `items-stretch`, where a stray top
                        margin shortens that button relative to its siblings. */}
                    <DialogFooter className="mt-2 flex-col items-stretch justify-center gap-2 sm:flex-wrap sm:justify-center sm:gap-3">
                        <Button
                            ref={revealButtonRef}
                            type="button"
                            data-playwright-id={revealButtonDataPlaywrightId}
                            className="h-auto w-full touch-manipulation whitespace-normal rounded-full px-5 py-2 text-center [@media(pointer:coarse)]:min-h-11 sm:order-3 sm:w-auto sm:min-w-[9.5rem]"
                            onClick={onReveal}
                        >
                            <Eye className="size-4" aria-hidden={true} />
                            {resolvedRevealText}
                        </Button>
                        {shouldShowEnablePreferenceButton ? (
                            <Button
                                type="button"
                                variant="secondary"
                                data-playwright-id={enablePreferenceButtonDataPlaywrightId}
                                className="mt-2 h-auto w-full touch-manipulation whitespace-normal px-4 py-2 text-center [@media(pointer:coarse)]:min-h-11 sm:order-2 sm:mt-0 sm:w-auto sm:min-w-[9.5rem]"
                                disabled={savingPreference}
                                onClick={handleEnablePreference}
                            >
                                {savingPreference ? resolvedEnablePreferenceSavingText : resolvedEnablePreferenceText}
                            </Button>
                        ) : null}
                        {/* PROFILE-1. The scope of "Always show everywhere" is stated here as well as in
                            the button, because for a logged-out visitor this copy is the ENTIRE
                            safeguard: the one screen that can reverse the choice is behind
                            authentication, so somebody who does not read this has no in-product way
                            back. Rendered only when the action is actually offered.

                            PROFILE-14 — it lives HERE, under the control it governs, and no longer in
                            the shared `DialogHeader`. It is the panel's longest paragraph (39 words
                            across two keys, against 13 for the paragraph about the decision the visitor
                            is actually making) and it was three elements above the button, separated
                            from it by the primary action and the whole footer gap. So the panel's
                            visual weight sat on the text most likely to be skipped, and anybody who
                            scanned straight to the buttons never passed the safeguard at all — the
                            opposite of what PROFILE-1 was closed to achieve. Same strings, same order,
                            same `isLoggedIn` condition: this is a re-render, not a re-mint.

                            `w-full sm:order-4` is what keeps PROFILE-16 intact. On the phone stack it
                            simply follows the button in DOM order. At `sm` the footer is a WRAPPING
                            row, and a full-basis item takes its own line — so it lands beneath the
                            button row instead of becoming a fourth item competing for the 520px
                            PROFILE-16 measured, and the painted button row is byte-identical.

                            `text-left`, per the row's second half: centred ragged-left prose is the
                            slowest setting to read and this is the paragraph that must be read. The
                            badge, subject and title stay centred — that cluster is doing the right job.
                            Alignment is a property of the paragraph rather than of a viewport, so the
                            locales that run ~25% longer than English inherit it unchanged. */}
                        {shouldShowEnablePreferenceButton ? (
                            <p
                                className="w-full max-w-2xl text-left text-sm leading-6 text-muted-foreground sm:order-4 sm:mx-auto"
                                data-playwright-id="sensitive-page-gate-scope"
                            >
                                {localizer("sensitive.page-warning.enable-preference.scope")}
                                {/* `PROFILE-39` (S7 C36): a signed-in member is told the change is
                                    reversible and where. The signed-out branch below keeps its own
                                    sentence and the inline sign-in. */}
                                {isLoggedIn ? (
                                    <>
                                        {" "}
                                        <span data-playwright-id="sensitive-page-gate-scope-reversible">
                                            {localizer("sensitive.page-warning.enable-preference.scope.signed-in")}
                                        </span>
                                    </>
                                ) : null}
                                {!isLoggedIn ? (
                                    <>
                                        {" "}
                                        {localizer("sensitive.page-warning.enable-preference.scope.signed-out")}
                                        {/* `PROFILE-15`, wave-12 Q25 option A. The sentence above tells a
                                            signed-out visitor to sign in first, and until now the screen
                                            offered exactly three controls — Continue, Always show
                                            everywhere, Go home — and none of them was sign in. It stated
                                            a precondition it declined to satisfy, to the one person for
                                            whom this copy is the ENTIRE safeguard (`PROFILE-1`).

                                            INLINE, in the sentence, and not a fourth peer in the decision
                                            row: growing or demoting that row was decided against in
                                            `designs/07-archive/runs/2026-08-14-ux-critique-remediation-wave-1-design.md` §3,
                                            and `PROFILE-19` below is bound by the same constraint.

                                            The DIALOG rather than `<a href="/login">`, and that is the
                                            return-to-this-URL half rather than a preference: `GET /login`
                                            takes no continuation parameter, so a plain link lands the
                                            visitor on `/home` afterwards and they have to find this
                                            profile again by hand. `LoginForm`'s
                                            `resolveSuccessfulLoginDestination` returns the explicit
                                            continuation when it is rendered as a modal, so passing the
                                            current relative URL brings them straight back here — to the
                                            same gate, now with a settings screen they can actually reach.

                                            Copy is REUSED, not minted: `account-access.login` is the tab
                                            label on the auth card, already in all 25 catalogs. It reads
                                            "Log In" where the sentence says "sign in first"; harmonising
                                            those two words is a 24-locale bill (rule 11) and was recorded
                                            rather than taken. */}
                                        {" "}
                                        {/* `PROFILE-67` (A35): `relative touch-hit-44`, the expander and
                                            not a taller line, so the sentence paints exactly as before
                                            while a thumb gets the 44px floor its four siblings have. It
                                            sits on the paragraph's LAST line, so its 10px of vertical
                                            reach cannot land on "Always show everywhere" above the
                                            paragraph (`PROFILE-18`'s separation). */}
                                        <button
                                            type="button"
                                            className="relative touch-hit-44 standard-link rounded-sm font-medium text-foreground outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
                                            data-playwright-id="sensitive-page-gate-sign-in"
                                            onClick={() => {
                                                openAccountAccessDialog(
                                                    localizer,
                                                    resolveContextualAccountAccessDialogOptions(localizer, {
                                                        ...withAccountAccessSurface(
                                                            ACCOUNT_ACCESS_DIALOG_COPY.safety,
                                                            "sensitive_page_gate",
                                                        ),
                                                        initialTab: "login",
                                                        continuation: getCurrentRelativeUrl(),
                                                    }),
                                                );
                                            }}
                                        >
                                            {localizer("account-access.login")}
                                        </button>
                                    </>
                                ) : null}
                            </p>
                        ) : null}
                        <Button
                            type="button"
                            variant="outline"
                            data-playwright-id={goHomeButtonDataPlaywrightId}
                            className="h-auto w-full touch-manipulation whitespace-normal px-4 py-2 text-center [@media(pointer:coarse)]:min-h-11 sm:order-1 sm:w-auto sm:min-w-[9.5rem]"
                            asChild
                        >
                            <a href={homeHref}>{resolvedGoHomeText}</a>
                        </Button>
                        {/* `PROFILE-19` (Q19). Declining used to cost the visitor their place: the gate
                            is a hard modal — no close button, Escape prevented, outside click prevented
                            — and its only exit was a navigation to the product's front page. Someone who
                            followed a link from search and decided not to view this profile was
                            deposited INTO a product they had not chosen to enter, which is the most
                            expensive possible answer to the cheapest possible decision.

                            ⛔ Rule 33 binds this absolutely. `goBackToPreviousEntry` is the sanctioned
                            traversal helper for a control whose whole meaning is "go back", and the
                            `fallbackUrl` is what makes it FALL BACK rather than pop blind: a bare
                            `history.back()` pops whatever is on top, which is how an overlay ate a
                            member's page on 2026-08-22 (`AGENTS/frontend-navigation-and-history.md`).
                            `scripts/check-history-writers.mjs` fails the build on a direct call.

                            ⛔ It is an inline link and NOT a fourth peer in the decision row — same
                            wave-1 §3 constraint as `PROFILE-15` above. `w-full sm:order-5` is the same
                            device the scope paragraph uses at `sm:order-4`: a full-basis item takes its
                            own line in the wrapping row, so the painted three-button row `PROFILE-16`
                            settled is byte-identical and this sits under it.

                            The link renders only when there IS an own entry to return to; a cold entry
                            sees "Go home" and nothing else, which is the correct answer for it. Copy is
                            `literal.back`, which already exists and already serves as a back affordance
                            on the login card. */}
                        {hasSomewhereToGoBackTo ? (
                            <button
                                type="button"
                                className="standard-link w-full rounded-sm py-2 text-center text-sm text-muted-foreground outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] [@media(pointer:coarse)]:min-h-11 sm:order-5"
                                data-playwright-id="sensitive-page-gate-go-back"
                                onClick={goBack}
                            >
                                {localizer("literal.back")}
                            </button>
                        ) : null}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
